import { useState, useCallback, useEffect, useRef } from "react";
import { Map, Marker, MapMouseEvent } from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { AddressMapProps, MapViewState, MarkerPosition } from "./types";
import { AddressAutocomplete } from "ui/components/Map/AddressAutocomplete";
import { AddressAutocompleteSuggestion } from "ui/components/Map/AddressAutocomplete/types";



const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;

export default function AddAddressMap(props: AddressMapProps) {
  const { onChange, initialPosition, formData, hideAutocomplete } = props;

  const [marker, setMarker] = useState<MarkerPosition | null>(
    initialPosition || null
  );
  const [address, setAddress] = useState<string>("");
  const [addressSearchQuery, setAddressSearchQuery] = useState<string>("");
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [viewState, setMapViewState] = useState<MapViewState>({
    latitude: initialPosition?.lat || 45.815,
    longitude: initialPosition?.lng || 15.9819,
    zoom: 13,
  });
  const isInternalUpdateRef = useRef(false);

  useEffect(() => {
    if (initialPosition) {
      setMarker(initialPosition);
      setMapViewState({
        latitude: initialPosition.lat,
        longitude: initialPosition.lng,
        zoom: 13,
      });
    }
  }, [initialPosition]);

  // Update map when form data changes (only when not updating from map)
  useEffect(() => {
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    if (formData) {
      const fullAddress = [
        formData.line1,
        formData.city,
        formData.state,
        formData.postalCode,
        formData.country,
      ]
        .filter(Boolean)
        .join(", ");

      if (fullAddress && fullAddress.trim() !== address.trim()) {
        setAddressSearchQuery(fullAddress);
      }
    }
  }, [formData]);

  const handleMapLoad = useCallback(() => {
    setIsMapLoaded(true);
  }, []);

  const parseAddressComponents = (features: any[]) => {
    if (!features || features.length === 0) return {};

    const feature = features[0];
    const context = feature.context || [];

    let country = "";
    let city = "";
    let state = "";
    let postalCode = "";
    let line1 = "";

    context.forEach((item: any) => {
      if (item.id.startsWith("country")) {
        country = item.text;
      } else if (item.id.startsWith("place")) {
        city = item.text;
      } else if (item.id.startsWith("region")) {
        state = item.text;
      } else if (item.id.startsWith("postcode")) {
        postalCode = item.text;
      }
    });

    line1 = feature.text || "";

    return { country, city, state, postalCode, line1 };
  };

  const handleMapClick = useCallback(
    async (e: MapMouseEvent) => {
      if (!isMapLoaded) return;
      const { lng, lat } = e.lngLat;
      setMarker({ lat, lng });

      try {
        const res = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
        );
        const data = await res.json();

        const fullAddress = data?.features?.[0]?.place_name ?? "Nepoznata adresa";
        isInternalUpdateRef.current = true;
        setAddress(fullAddress);
        setAddressSearchQuery(fullAddress);

        const components = parseAddressComponents(data.features);

        if (onChange) {
          onChange({
            address: fullAddress,
            latitude: lat,
            longitude: lng,
            ...components,
          });
        }
      } catch {
        setAddress("Greška pri dohvaćanju adrese");
      }
    },
    [isMapLoaded, onChange]
  );

  const handleAutocompleteSelect = useCallback((suggestion: AddressAutocompleteSuggestion) => {
    const [lng, lat] = suggestion.center;
    setMarker({ lat, lng });

    // Update map view to center on selected location
    setMapViewState({
      latitude: lat,
      longitude: lng,
      zoom: 15,
    });

    const context = suggestion.context || [];
    let country = "";
    let city = "";
    let state = "";
    let postalCode = "";

    context.forEach((item: any) => {
      if (item.id.startsWith("country")) {
        country = item.text;
      } else if (item.id.startsWith("place")) {
        city = item.text;
      } else if (item.id.startsWith("region")) {
        state = item.text;
      } else if (item.id.startsWith("postcode")) {
        postalCode = item.text;
      }
    });

    const fullAddress = suggestion.place_name;
    isInternalUpdateRef.current = true;
    setAddress(fullAddress);
    setAddressSearchQuery(fullAddress);

    if (onChange) {
      onChange({
        address: fullAddress,
        latitude: lat,
        longitude: lng,
        country: country,
        city: city,
        state: state,
        postalCode: postalCode,
        line1: suggestion.text || "",
      });
    }
  }, [onChange]);

  /*
    Bez tokena Mapbox ne nacrta ništa i ostane bijela ploha, po kojoj se ne vidi da
    nedostaje postavka. Zato se to kaže naglas umjesto da se traži po konzoli.
  */
  if (!MAPBOX_TOKEN) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-slate-100 p-8">
        <div className="max-w-sm text-center">
          <p className="text-sm font-semibold text-slate-700">Karta nije dostupna</p>

          <p className="text-sm text-slate-500 mt-2">
            Nedostaje <span className="font-mono text-xs">VITE_MAPBOX_TOKEN</span> u{" "}
            <span className="font-mono text-xs">.env</span> datoteci. Adresa se i dalje može
            upisati ručno u polja desno.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0">
      <Map
        mapboxAccessToken={MAPBOX_TOKEN}
        mapLib={mapboxgl}
        {...viewState}
        onMove={(evt) => setMapViewState(evt.viewState)}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        onLoad={handleMapLoad}
        onClick={handleMapClick}
      >
        {marker && (
          <Marker latitude={marker.lat} longitude={marker.lng}>
            <div className="text-2xl text-red-600">📍</div>
          </Marker>
        )}
      </Map>

      {/* Address Autocomplete */}
      {!hideAutocomplete && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 w-96 z-10">
          <AddressAutocomplete
            value={addressSearchQuery}
            onChange={setAddressSearchQuery}
            onSelect={handleAutocompleteSelect}
            placeholder="Pretraži adresu..."
          />
        </div>
      )}

      {/* Address Info Panel */}
      {address && (
        <div className="absolute top-4 right-4 rounded-lg bg-white p-3 shadow-lg max-w-xs">
          <p className="text-xs text-gray-500 mb-1">Odabrana adresa</p>
          <p className="text-sm font-medium line-clamp-2">{address}</p>
        </div>
      )}
    </div>
  );
}
